import React, { useState, useEffect } from 'react';
import { appendPeriods } from '../../api';
import { C } from './shared';
import PeriodsTable from './PeriodsTable';

export default function PeriodsPopup({ rowId, rowName, periods, onClose, onAppended }) {
  // `periods === null` — масив ще їде з сервера (список його не возить, попап
  // просить окремим запитом). Порожній масив означає «вікон немає» — це різні
  // стани, і плутати їх не можна: користувач побачив би «вікон немає» там, де
  // вони просто ще не приїхали.
  const periodsLoading = periods == null;
  const [localPeriods, setLocalPeriods] = useState(Array.isArray(periods) ? periods : []);
  // Масив приходить ПІСЛЯ першого рендера, тож стан треба підхопити — useState
  // читає початкове значення рівно один раз.
  useEffect(() => {
    if (Array.isArray(periods)) setLocalPeriods(periods);
  }, [periods]);
  const [newPeriodsForm, setNewPeriodsForm] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  function addRow() {
    setNewPeriodsForm(prev => [...prev, { start_date: '', end_date: '' }]);
  }

  function removeRow(i) {
    setNewPeriodsForm(prev => prev.filter((_, idx) => idx !== i));
  }

  function updateRow(i, field, value) {
    setNewPeriodsForm(prev => prev.map((p, idx) => idx === i ? { ...p, [field]: value } : p));
  }

  async function handleSubmit() {
    const validPeriods = newPeriodsForm.filter(p => p.start_date && p.end_date);
    if (validPeriods.length === 0) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      const resp = await appendPeriods(rowId, validPeriods);
      const data = resp.data;
      setLocalPeriods(Array.isArray(data.periods_results) ? data.periods_results : []);
      setNewPeriodsForm([]);
      onAppended(data);
    } catch (e) {
      const status = e.response?.status;
      if (status === 408) setErrorMsg('Append timed out (>180s)');
      else if (status === 404) setErrorMsg('Row not found');
      else if (status === 422) setErrorMsg(e.response?.data?.detail || 'Invalid periods');
      else setErrorMsg(e.message || 'Failed');
    } finally {
      setLoading(false);
    }
  }

  const hasValidRows = newPeriodsForm.some(p => p.start_date && p.end_date);

  const overlayStyle = {
    position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
    background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000,
  };

  const modalStyle = {
    background: '#fff', borderRadius: 12, padding: 24,
    maxWidth: 1180, width: '94%', maxHeight: '85vh', overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
    position: 'relative',
  };

  const inputStyle = {
    padding: '6px 8px', border: '1px solid #E2E8F0', borderRadius: 6,
    fontSize: 12, color: '#0F172A', background: '#fff', width: '100%',
    boxSizing: 'border-box', outline: 'none',
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0F172A' }}>
            {rowName ? `${rowName} — ` : `Стратегія #${rowId} — `}
            {periodsLoading ? 'periods…' : `periods (${localPeriods.length})`}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', fontSize: 22, cursor: 'pointer',
              color: '#64748B', lineHeight: 1, padding: '2px 6px',
            }}
            title="Close"
          >×</button>
        </div>

        {/* Periods table — спільний компонент із сторінкою результату */}
        {periodsLoading ? (
          <p style={{ color: C.textSm, fontSize: 13, marginBottom: 16 }}>Завантаження вікон…</p>
        ) : localPeriods.length === 0 ? (
          <p style={{ color: C.textSm, fontSize: 13, marginBottom: 16 }}>No periods calculated yet.</p>
        ) : (
          <div style={{ marginBottom: 20 }}>
            {/* Підсумок під колонками — той самий, що стоїть у рядку зовнішньої
                таблиці (запит менеджера 07.09): інакше попап показував окремі
                вікна, а звести їх у число доводилось очима. */}
            <PeriodsTable periods={localPeriods} showAverages />
          </div>
        )}

        {/* Add period form */}
        <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: 16 }}>
          <h4 style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: '#334155' }}>
            + Add new periods
          </h4>

          {newPeriodsForm.map((p, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, marginBottom: 8 }}>
              <input
                type="date"
                value={p.start_date}
                onChange={e => updateRow(i, 'start_date', e.target.value)}
                style={inputStyle}
                placeholder="Start date"
              />
              <input
                type="date"
                value={p.end_date}
                onChange={e => updateRow(i, 'end_date', e.target.value)}
                style={inputStyle}
                placeholder="End date"
              />
              <button
                onClick={() => removeRow(i)}
                style={{
                  padding: '6px 10px', background: '#FEE2E2', border: '1px solid #FECACA',
                  borderRadius: 6, cursor: 'pointer', color: C.danger, fontWeight: 700,
                  fontSize: 14,
                }}
                title="Remove row"
              >×</button>
            </div>
          ))}

          <button
            onClick={addRow}
            style={{
              padding: '6px 14px', background: '#F1F5F9', border: '1px solid #E2E8F0',
              borderRadius: 7, cursor: 'pointer', fontSize: 12, color: '#475569',
              fontWeight: 600, marginTop: 4,
            }}
          >+ Add period</button>
        </div>

        {/* Submit area */}
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.primary, fontSize: 13 }}>
              <span style={{
                display: 'inline-block', width: 16, height: 16,
                border: '2px solid #93C5FD', borderTopColor: C.primary,
                borderRadius: '50%', animation: 'ppSpin 0.7s linear infinite',
              }} />
              Calculating sims…
            </div>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!hasValidRows}
              style={{
                padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                cursor: hasValidRows ? 'pointer' : 'not-allowed',
                background: hasValidRows ? C.primary : '#CBD5E1',
                color: '#fff', border: 'none',
                opacity: hasValidRows ? 1 : 0.7,
              }}
            >Розрахувати</button>
          )}
          {errorMsg && (
            <span style={{
              padding: '6px 12px', background: '#FEE2E2', border: '1px solid #FECACA',
              borderRadius: 7, fontSize: 12, color: C.danger, fontWeight: 600,
            }}>
              ✗ {errorMsg}
            </span>
          )}
        </div>

        <style>{`@keyframes ppSpin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}
